import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { apiFetch } from '../lib/apiClient';
import {
  BookOpen,
  Clock,
  CheckCircle2,
  Lock,
  Play,
} from 'lucide-react';

export default function RoadmapPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [hideProgression, setHideProgression] = useState(() => {
    return localStorage.getItem('hideProgression') === 'true';
  });

  const toggleHideProgression = () => {
    const newVal = !hideProgression;
    setHideProgression(newVal);
    localStorage.setItem('hideProgression', newVal);
  };

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const message = data?.message || data?.error || 'Unable to load profile.';
          throw new Error(message);
        }

        if (isMounted) {
          setProfile(data.profile || data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load profile.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  const defaultRoadmap = {
    styleTrack: 'Concept Art',
    modules: [
      { id: 'm1', title: 'Drawing Fundamentals', progress: 100, status: 'completed', lessons: 8, duration: '10 min' },
      { id: 'm2', title: 'Fundamentals of Perspective', progress: 45, status: 'current', lessons: 12, duration: '30 min' },
      { id: 'm3', title: 'Color Theory & Value', progress: 0, status: 'locked', lessons: 10, duration: '45 min' },
    ],
  };

  const resolvedRoadmap = (() => {
    const roadmap = profile?.currentRoadmap;
    const available = roadmap?.availableExercises || [];
    const completed = roadmap?.completedExercises || [];

    if (!roadmap || (!available.length && !completed.length)) {
      return defaultRoadmap;
    }

    const completedIds = completed.map(c => c.exerciseId);
    const allExercises = [...completed, ...available];

    return {
      styleTrack: roadmap.styleTrack || profile?.styleTrack || defaultRoadmap.styleTrack,
      progressionPath: roadmap.progressionPath || '',
      modules: allExercises.map((exercise) => {
        const isCompleted = completedIds.includes(exercise.exerciseId);
        return {
          id: exercise.exerciseId,
          title: exercise.title,
          progress: isCompleted ? 100 : 0,
          status: isCompleted ? 'completed' : 'current',
          lessons: exercise.learningObjectives?.length || 1,
          duration: `${exercise.duration} min`,
          difficulty: exercise.difficultyLevel,
          targetSkills: exercise.targetSkills || [],
        };
      }),
    };
  })();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,36%)]" />;
      case 'current':
        return <Play className="w-4 h-4 text-[hsl(234,89%,63%)]" />;
      default:
        return <Lock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8 space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-start justify-between">
            <div className="flex-1 mr-8">
              <div className="h-8 w-48 bg-slate-200/60 rounded animate-pulse mb-2"></div>
              <div className="h-4 w-64 bg-slate-200/60 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-36 bg-slate-200/60 rounded-full animate-pulse shrink-0"></div>
          </div>

          {/* Summary Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-white rounded-xl border border-slate-100 shadow-sm animate-pulse p-5">
                <div className="space-y-3">
                  <div className="h-3 w-24 bg-slate-200/60 rounded"></div>
                  <div className="h-6 w-32 bg-slate-200/60 rounded"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Roadmap List Skeleton */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4 animate-pulse">
            <div className="h-5 w-40 bg-slate-200/60 rounded mb-6"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-200/60 bg-white flex flex-col md:flex-row md:items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200/60 shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <div className="h-4 w-48 bg-slate-200/60 rounded"></div>
                      <div className="h-4 w-16 bg-slate-200/60 rounded-full"></div>
                    </div>
                    <div className="flex gap-2">
                      <div className="h-3 w-12 bg-slate-200/60 rounded"></div>
                      <div className="h-3 w-32 bg-slate-200/60 rounded"></div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-200/60 rounded mt-2"></div>
                  </div>
                  <div className="h-8 w-24 bg-slate-200/60 rounded-full shrink-0"></div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8 flex items-center justify-center">
          <p className="text-red-600 text-sm">{error}</p>
        </main>
      </div>
    );
  }

  const completedCount = resolvedRoadmap.modules.filter((m) => m.progress === 100).length;
  const totalModules = resolvedRoadmap.modules.length;
  const overallProgress = totalModules
    ? Math.round((completedCount / totalModules) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="roadmap-page">
      <DashboardSidebar />

      <main className="lg:ml-60 p-4 pt-24 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 mr-8">
            <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">
              Your Roadmap
            </h1>
            <p className="text-slate-600 mt-0.5">
              Track every exercise in your {resolvedRoadmap.styleTrack} journey.
            </p>
            {resolvedRoadmap.progressionPath && (
              <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 leading-relaxed shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700 block">Curriculum progression</span>
                  <button 
                    onClick={toggleHideProgression}
                    className="text-xs text-slate-400 hover:text-slate-600 underline focus:outline-none"
                  >
                    {hideProgression ? 'Show' : 'Hide'}
                  </button>
                </div>
                {!hideProgression && (
                  <p className="text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100">
                    {resolvedRoadmap.progressionPath}
                  </p>
                )}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            className="rounded-full shrink-0"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-elevated border-0">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">Track</p>
                <p className="text-lg font-semibold text-[hsl(222,47%,11%)] flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                  {resolvedRoadmap.styleTrack}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-elevated border-0">
            <CardContent className="p-5">
              <p className="text-xs text-slate-500 mb-1">Exercises Completed</p>
              <p className="text-2xl font-bold text-[hsl(222,47%,11%)]">
                {completedCount}/{totalModules}
              </p>
            </CardContent>
          </Card>
          <Card className="card-elevated border-0">
            <CardContent className="p-5">
              <p className="text-xs text-slate-500 mb-2">Overall Roadmap Progress</p>
              <div className="flex items-center gap-3">
                <Progress value={overallProgress} className="h-2 flex-1" />
                <span className="text-sm font-semibold text-[hsl(222,47%,11%)]">
                  {overallProgress}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Roadmap List */}
        <Card className="card-elevated border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Outfit']">
              Roadmap exercises
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resolvedRoadmap.modules.length === 0 ? (
              <p className="text-sm text-slate-500">
                Your personalized roadmap is not available yet. Complete the onboarding to generate it.
              </p>
            ) : (
              <div className="space-y-3">
                {resolvedRoadmap.modules.map((module) => (
                  <div
                    key={module.id}
                    className="p-4 rounded-xl border border-slate-200/70 bg-white flex flex-col md:flex-row md:items-center gap-3 cursor-pointer hover:border-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,63%)]/5 transition-colors"
                    onClick={() => navigate(`/exercise/${module.id}`)}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="mt-1">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100">
                          {getStatusIcon(module.status)}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm text-[hsl(222,47%,11%)] truncate">
                            {module.title}
                          </h3>
                          {module.difficulty && (
                            <Badge variant="outline" className="text-[10px] px-2 py-0 rounded-full">
                              Difficulty {module.difficulty}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mb-2">
                          <span>{module.duration}</span>
                          {module.targetSkills.length > 0 && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-slate-400" />
                              <span>
                                Focus:{' '}
                                {module.targetSkills.join(', ')}
                              </span>
                            </>
                          )}
                        </div>
                        <Progress value={module.progress} className="h-1.5" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-start md:self-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs"
                      >
                        View details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

